import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {RootState, useAppDispatch, useAppSelector} from "../../store";
import TicketService from "../../api/ticketService";
import WishService from "../../api/wishService";
import {IWish, WishStatus, WishType} from "../../store/wish/Helper";
import {removeWishFromWishlist} from "../../store/wishlist/Slice";
import {removeWish} from "../../store/wish/Slice";

export interface WinnerEntry {
    id: string;
    code: string;
    full_name: string;
    recipient_name?: string;
    profile_picture?: string;
}

const POLL_INTERVAL = 15000; // 15 seconds in ms
const DRAW_DURATION = 180000; // 3 minutes in ms
const POLL_START_DELAY = 75000; // 1.5 minutes in ms

const useWishCountdown = (wish: IWish) => {
    const [countDown, setCountDown] = useState<number | null>(0);
    const [drawStarted, setDrawStarted] = useState(false);
    const serverTime = useAppSelector((state: RootState) => state.appReducer.serverTime);
    const [ticketsArray, setTicketsArray] = useState<WinnerEntry[]>([]);
    const [winner, setWinner] = useState<WinnerEntry | null>(null);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const initialFetch = useRef(false)
    const dispatch = useAppDispatch()
    const isVoucher = useMemo(() => wish.type === WishType.VOUCHER, [wish.type])

    const fetchWishDetails = useCallback(() => {
        if (isVoucher) {
            dispatch(removeWishFromWishlist(wish.id))
            dispatch(removeWish(wish.id))
            return
        }
        WishService.fetchWishDetails(wish.id)
            .then(({data}: { data: IWish }) => {
                if (data.winner !== null) {
                    setWinner({
                        full_name: data.winner.ticket?.user?.full_name ?? '',
                        recipient_name: data.winner.ticket?.recipient_name ?? undefined,
                        id: data.winner.ticket?.id ?? '',
                        code: data.winner.ticket?.code ?? '',
                        profile_picture: data.winner.ticket?.user?.profile_picture ?? '',
                    });
                }
            })
            .catch(err => console.error("Error fetching wish details", err));
    }, [wish.id, isVoucher, dispatch])

    // Calculate the countdown.
    const calculateCountdown = useCallback(() => {
        if (wish.end_time && serverTime) {
            const start = new Date(wish.end_time).getTime();
            const current = new Date(serverTime).getTime();
            const elapsed = current - start;

            if (elapsed < 0) {
                // Before the draw: show time until start.
                setCountDown(-elapsed);
            } else if (elapsed < DRAW_DURATION) {
                // Draw in progress: mark as started and show remaining time.
                setDrawStarted(true);
                setCountDown(DRAW_DURATION - elapsed);
            } else {
                // After 3 minutes: clear countdown.
                setCountDown(null)
                setTicketsArray([])
                setDrawStarted(false)
            }
        }
    }, [wish.end_time, serverTime]);

    useEffect(() => {
        calculateCountdown();
    }, [calculateCountdown]);

    // Fetch tickets once the draw has started.
    useEffect(() => {
        if (drawStarted && !isVoucher) {
            TicketService.fetchTicketsForWish(wish.id)
                .then(res => {
                    setTicketsArray(res.data);
                })
                .catch(err => console.error("Error fetching tickets", err));
        }
    }, [drawStarted, wish.id, isVoucher]);

    // Start polling when the draw is in progress and after the POLL_START_DELAY has passed.
    useEffect(() => {
        if (winner || wish.status !== WishStatus.ACTIVE) return;

        if (isVoucher && drawStarted && !initialFetch.current) {
            fetchWishDetails();
            initialFetch.current = true;
        }

        if (!initialFetch.current && countDown === null) {
            fetchWishDetails();
            initialFetch.current = true;
        }

        // Start polling when the draw is in progress and the countdown has reached the poll delay.
        if (drawStarted && countDown !== null && countDown <= (DRAW_DURATION - POLL_START_DELAY) && !pollIntervalRef.current) {
            // Trigger immediate fetch once when countDown is null and it hasn't been fetched yet.
            if (!initialFetch.current) {
                fetchWishDetails();
                initialFetch.current = true;
            }
            pollIntervalRef.current = setInterval(fetchWishDetails, POLL_INTERVAL);
        }
    }, [drawStarted, winner, countDown, fetchWishDetails, wish.status, isVoucher]);

    // Clear the polling interval when the draw is over or a winner is found.
    useEffect(() => {
        if ((winner || countDown === null) && pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, [winner, countDown]);

    // Cleanup on unmount.
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    return {countDown, drawStarted, setDrawStarted, ticketsArray, setTicketsArray, winner};
};

export default useWishCountdown;
